import { useEffect, useRef } from 'react';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useQueryParamHelper } from '@shared/core/queryString';
import type { UseSharedCatalogMutationReturn } from '../../Catalog.controller';

import { useGetCatalogProductDetailsLazyQuery, CustomItemDataType } from '@graphql/generated';
import type { AddRegistryMethodType, PagesForTelemetry } from '../../Catalog.telemetry.types';
import type { CashFundDialogContext } from '../../components/CashFundDialogProvider';
import { useRouteMatch, useLocation } from '@react-router';
import { useCatalogRoutePaths } from '@apps/registry/common/components/Catalog/Catalog.routes';
import { generateRegistryItemMetadataSourceFromCatalog } from '../../utils/registryItemMetadata';
import { isCashOrCharityCatalogItem, isDeliverableCatalogItem } from '../../utils/interpretItemType';

type UseAutoAddCatalogItemOnMountArgs = {
  eventId: Maybe<string>;
  addCatalogItem: UseSharedCatalogMutationReturn['addCatalogItemToRegistry'];
  openAddCatalogCashFundDialog: CashFundDialogContext['openAddCatalogCashFundDialog'];
  openCustomCashFundDialog: CashFundDialogContext['openCustomCashFundDialog'];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toInteger = (val: any) => {
  const maybeInt = Number.parseInt(val);
  return isNaN(maybeInt) ? undefined : maybeInt;
};

/**
 * It is "safe" to remove the `intent` query param only when the
 * current location is stable (aka not in between url changes)
 *
 * eg. if there are any `<Redirect/>s on the initial render,
 * we only want to fire the update after we get the new location
 *
 */
const useSafeRemoveIntentQueryParam = (intentQueryParamValue: string | undefined) => {
  const updateSearchParam = useUpdateSearchParam();
  const location = useLocation();
  useEffect(
    () => {
      // always remove the intent query param if present
      if (intentQueryParamValue === undefined) {
        return;
      }

      // Wrap the actual call to update the search param in a `setTimeout`
      // so that we can effectively cancel it if needed
      const timeoutId = setTimeout(() => {
        updateSearchParam('intent', null, 'replace');
      }, 50);

      return () => {
        clearTimeout(timeoutId);
      };
    },
    // include react router location value so that we only update search
    // param after the location has updated

    // eslint-disable-next-line
    [intentQueryParamValue, location],
  );
};

/**
 * When a user tries to add a registry item but hasn't created an event, they'll be sent to `/createwedding`.
 *
 * After they create a wedding, they'll be redirected back to their previous shop location.
 *
 * This will handle auto-adding catalog items for certain routes:
 *  - catalog (root, department, cat, subcat)
 *  - curations
 *
 * This does not handle auto-adding when user is redirected to an actual PDP (the url contains the product id instead of
 * having it set as a query param)
 */
export const useAutoAddCatalogItemOnMount = (args: UseAutoAddCatalogItemOnMountArgs) => {
  const { eventId, addCatalogItem, openAddCatalogCashFundDialog, openCustomCashFundDialog } = args;

  const { catalogRoutes } = useCatalogRoutePaths();
  const { getValueString } = useQueryParamHelper();
  const pdpPageMatch = useRouteMatch<{ elementId: string }>(catalogRoutes.catalogItem.paths);

  // Keep intent stored in a ref, so we can remove it from the URL search params
  const intentRef = useRef(getValueString('intent'));

  // Standard product
  const paramQty = useRef(getValueString('qty')).current;
  const paramProductId = useRef(getValueString('productId')).current;

  // Cash fund products
  const paramIsCustom = useRef(getValueString('custom')).current;
  const paramFundType = useRef(getValueString('fundType')).current;

  const paramItemPosition = useRef(toInteger(getValueString('itemPosition')));
  const paramTelemetryPage = useRef((getValueString('telemetryPage') || 'cwMissingParam') as PagesForTelemetry).current;
  const paramTelemetryMethod = useRef((getValueString('telemetryMethod') || 'cwMissingParam') as AddRegistryMethodType).current;

  const [getProductData] = useGetCatalogProductDetailsLazyQuery({
    batchMode: 'fast',
    onCompleted: data => {
      const catalogItem = data.productCatalogItemById;
      if (catalogItem) {
        const maybeItemPosition = typeof paramItemPosition.current === 'number' && paramItemPosition.current >= 0 ? paramItemPosition.current : undefined;

        if (isDeliverableCatalogItem(catalogItem)) {
          const maybeQuantity = toInteger(paramQty);
          const metadataSource = generateRegistryItemMetadataSourceFromCatalog(paramTelemetryMethod, paramTelemetryPage);
          addCatalogItem({
            source: 'catalog',
            payload: {
              qty: Math.max(typeof maybeQuantity === 'number' ? maybeQuantity : 1, 1),
              product: catalogItem,
              metadata: {
                source: metadataSource,
                sourceMetadata: {
                  scannedPrice: catalogItem.price?.floatingPointDecimalString,
                },
              },
              itemPosition: maybeItemPosition,
              telemetryArgs: {
                method: paramTelemetryMethod,
                page: paramTelemetryPage,
              },
            },
          });
        }

        if (isCashOrCharityCatalogItem(catalogItem)) {
          openAddCatalogCashFundDialog({
            addMethodType: paramTelemetryMethod,
            product: catalogItem,
            itemPosition: maybeItemPosition,
          });
        }
      }
    },
  });

  useSafeRemoveIntentQueryParam(getValueString('intent'));

  useEffect(
    () => {
      // 1. Once we confirmed that the provided `eventId` exists, we can then go ahead with adding the catalog item
      if (!eventId || intentRef.current !== 'add') {
        return;
      }

      // prioritize the `productId` param since we grab that right on page mount
      const maybeCatalogItemId = paramProductId || pdpPageMatch?.params.elementId;
      if (maybeCatalogItemId) {
        // 2a. maybe item from product grid or PDP
        //    Verify product exists first before attempting to add
        getProductData({
          variables: {
            catalogItemId: maybeCatalogItemId,
            withPdpSections: false,
            withProductRating: false,
            withInventory: false,
          },
        });
      } else if (paramIsCustom && paramFundType && paramFundType in CustomItemDataType) {
        // 2b. User previously tried to add a custom charity/cash fund
        openCustomCashFundDialog({ addMethodType: 'custom', fundType: CustomItemDataType[paramFundType as CustomItemDataType.cash | CustomItemDataType.charity] });
      }

      intentRef.current = undefined;
    },
    // eslint-disable-next-line
    [eventId],
  );
};
